// Boot: WebGL2 check → renderer → title (benchmark) → broadcast.
import './style.css';
import { params } from './app/params';
import { appState } from './app/appState';
import { createLoop } from './app/loop';
import { DebugHub } from './app/debug';
import { startRaceFeed } from './app/raceFeed';
import { randomSource } from './app/random';
import { createRenderer, hasWebGL2 } from './world/renderer';
import { QualityManager, percentile, tierFromBenchmark } from './world/quality';
import { createPostChain } from './world/post/composer';
import { PlaceholderScene } from './world/placeholderScene';
import { Scene } from 'three';
import { assets } from './world/assets';
import { Environment, environmentStage } from './world/environment';
import { applyDebugView } from './world/environment/views';
import { mountHud } from './hud/mount';
import { createAudio } from './audio';
import { effect } from '@preact/signals';
import { Director, applyCamera } from './director/director';
import { ReplayBadge } from './director/replayBadge';
import { CatField } from './world/cats/catField';
import { CatnipTrip } from './world/trip';
import { Vector3 } from 'three';
import { BENCH_SEEK_S, BenchRun } from './app/bench';
import type { TagPosition } from './hud/NameTags';
import { realScheduler } from './signal/sources/types';

const BENCH_MS = 1500;

async function boot(): Promise<void> {
  const p = params();
  if (!hasWebGL2()) {
    document.getElementById('no-webgl')!.hidden = false;
    appState.go('noWebGL');
    return;
  }

  const app = document.getElementById('app')!;
  const hud = document.getElementById('hud')!;
  const handle = createRenderer(app);
  // ?capture=1 renders 60 fps race time however slowly frames are drawn: fixed tier, no auto-tuning.
  const quality = new QualityManager(p.quality ?? 'medium', p.quality === null && !p.bench && !p.capture);
  if (p.capture) document.documentElement.dataset.capture = '';
  const audio = createAudio(randomSource(p.seed));
  const hudHandle = mountHud(hud, {
    rand: randomSource(p.seed === null ? null : p.seed + 1),
    onStart: () => { audio.unlock(); hudHandle.store.muted.value = false; appState.go('broadcast'); },
  });
  const progress = hudHandle.store.loadProgress;
  progress.value = 0;
  const scene = new Scene();
  try {
    // Loading steps: stadium textures (to 30%), the cats (to 85%), shaders (to 100%).
    await assets.load([environmentStage()], (e) => { progress.value = 0.3 * e.fraction; });
  } catch (err) {
    console.warn('Stadium textures failed to load; using plain materials.', err);
  }
  // The stadium follows the real time in Joutseno; ?time= starts its clock at another moment.
  const clockOffset = p.time === null ? 0 : p.time - Date.now();
  const environment = new Environment(handle.renderer, scene, assets, quality.settings, () => Date.now() + clockOffset);
  progress.value = 0.3;
  let cats: CatField | null = null;
  try {
    cats = await CatField.create(handle.renderer, quality.settings);
    scene.add(cats.group);
  } catch (err) {
    console.warn('The cats failed to load; showing placeholder runners.', err);
  }
  progress.value = 0.85;
  const world = new PlaceholderScene(scene, cats === null);
  cats?.setCamera(world.camera);
  const post = createPostChain(handle.renderer, world.scene, world.camera, quality.settings);
  post.addEffect(environment.grade);
  const trip = new CatnipTrip({ scene, cats, environment, container: app, camera: world.camera });
  post.addEffect(trip.effect);
  await trip.compile(handle.renderer).catch((err) => console.warn('Trip shaders compile on first use.', err));
  progress.value = 1;
  const debug = new DebugHub(p.debug, quality, () => world.camera, { seed: p.seed, moment: p.moment });
  debug.setAudio(() => audio.state());
  const director = new Director();
  const replayBadge = new ReplayBadge(app);
  debug.setDirector(() => director.state);
  debug.setTrip(() => trip.featuredInfo());
  debug.setSky(() => environment.skyInfo());
  debug.setPodium((id) => cats?.actors.find((a) => a.def.id === id)?.podiumBehaviour ?? null);

  handle.onResize((w, h) => { world.setAspect(w / Math.max(1, h)); post.setSize(w, h); });
  quality.onChange((s) => { cats?.setTier(s); environment.applyTier(s); post.applyTier(s); handle.setRenderScale(quality.renderScale); });

  // ?bench=1 always measures the same stretch of the bundled recording.
  // In capture mode the sources read the loop's frame-stepped time, so frames arrive at the pace
  // race time advances; their timers stay real.
  let loopNow = (): number => 0;
  const captureScheduler = p.capture ? { ...realScheduler, now: () => loopNow() } : undefined;
  const { broadcast } = startRaceFeed(p.bench ? { ...p, source: 'replay', seek: BENCH_SEEK_S, speed: 1 } : p, captureScheduler, (m, seekMs) => debug.setMoments(m, seekMs));
  debug.setRace(broadcast);
  const loop = createLoop(broadcast, { fixedStepMs: p.capture ? 1000 / 60 : undefined });
  loopNow = () => loop.now();
  loop.setClock((wallMs) => broadcast.clock(wallMs));
  // Benchmark state: frame times collected while the title shows, unless ?quality fixed the tier.
  let benchTimes: number[] | null = p.quality === null && !p.autostart && !p.bench && !p.capture ? [] : null;
  const benchRun = p.bench ? new BenchRun((r) => { debug.setBench(r); console.info('Cat Derby benchmark', JSON.stringify(r)); }) : null;
  let benchElapsed = 0;

  // Name tags: each cat's head anchor projected through the current camera.
  if (cats) {
    const field = cats;
    const v = new Vector3(), toCat = new Vector3();
    hudHandle.setTagSource(() => {
      const { width, height } = handle.size();
      const cam = world.camera;
      return field.actors.map((a): TagPosition => {
        a.tagAnchor(v);
        const dist = toCat.copy(v).sub(cam.position).length();
        v.project(cam);
        const visible = v.z > -1 && v.z < 1 && Math.abs(v.x) < 1.05 && Math.abs(v.y) < 1.05 && dist < 140;
        return { id: a.def.id, x: (v.x * 0.5 + 0.5) * width, y: (-v.y * 0.5 + 0.5) * height, visible, closeUp: dist < 4 };
      });
    });
  }
  effect(() => audio.setMuted(hudHandle.store.muted.value));
  effect(() => audio.setVolume(hudHandle.store.volume.value));
  // During the director's finish video the world shows the replayed race in slow motion: its
  // animation clock runs behind real time by the time the slow motion took.
  let replayLag = 0;
  loop.add(({ snapshot, events, peek, dt, timeS }) => {
    debug.record(snapshot, events);
    const store = hudHandle.store;
    applyCamera(world.camera, director.update(snapshot, events, peek, dt, {
      choice: store.camera.value, followed: store.followed.value, restrained: store.restrained.value,
    }));
    const replay = director.replay;
    replayBadge.update(!!replay);
    const scene = replay?.snapshot ?? snapshot;
    const sceneDt = dt * (replay?.rate ?? 1);
    replayLag += dt - sceneDt;
    const sceneT = timeS - replayLag;
    world.update(scene, sceneDt, sceneT);
    // A fixed ?view= for checking the stadium keeps its camera, and a viewer's own camera choice
    // is never pulled away; otherwise the trip frames its highlight over the director's shot.
    const debugView = applyDebugView(world.camera);
    trip.takeCamera = !debugView && store.camera.value === 'director' && !replay;
    trip.showCallouts = store.started.value;
    trip.update(snapshot, events, peek, dt, timeS);
    cats?.update(scene, sceneDt, sceneT);
    environment.update(scene, replay ? [] : events, sceneDt, sceneT, world.camera);
  });
  loop.add(({ dt, dtMs }) => {
    // Tier and render-scale changes resize the canvas, which wipes it: make them before drawing,
    // never between drawing and the browser showing the frame (a blank, dark flash).
    if (benchTimes) {
      if (debug.frames > 10) { benchTimes.push(dtMs); benchElapsed += dtMs; } // skip warm-up frames
      if (benchElapsed >= BENCH_MS) {
        quality.set(tierFromBenchmark(benchTimes));
        debug.setBench({ p50: percentile(benchTimes, 0.5), p95: percentile(benchTimes, 0.95), p99: percentile(benchTimes, 0.99), frames: benchTimes.length });
        benchTimes = null;
      }
    } else if (!p.capture && quality.sample(dtMs)) {
      handle.setRenderScale(quality.renderScale);
    }
    handle.renderer.info.reset();
    post.render(dt);
    trip.renderInset(handle.renderer);
    debug.frame(dtMs, handle.renderer);
    benchRun?.frame(dtMs, handle.renderer.info.render.calls, handle.renderer.info.render.triangles, { tier: quality.tier, renderScale: quality.renderScale });
  });
  loop.add(({ snapshot, events, dt }) => audio.update(snapshot, events, dt));
  loop.add(({ snapshot, events, timeS }) => {
    hudHandle.update(snapshot, events, timeS * 1000, { kind: broadcast.kind, label: broadcast.label, connection: broadcast.connection });
  });
  loop.start();

  if (p.autostart || p.bench) {
    hudHandle.start();
    appState.go('broadcast');
    return;
  }
  appState.go('title');
}

void boot();
